// AnimatedPageTransition.tsx
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

interface Props {
  children: React.ReactNode;
}

export const AnimatedPageTransition: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] =
    useState<React.ReactNode>(children);
  const [animationClass, setAnimationClass] = useState<string>("");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPathRef = useRef<string>("");
  const nextChildrenRef = useRef<React.ReactNode>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    // Первый рендер или тот же путь
    if (!prevPath || prevPath === currentPath) {
      prevPathRef.current = currentPath;
      if (displayChildren !== children) {
        setDisplayChildren(children);
      }
      return;
    }

    // Если уже идет анимация, не начинаем новую
    if (isTransitioning) return;

    // Определяем направление анимации
    const getDirection = () => {
      const tabOrder = ["home", "favorites", "results", "responses", "profile"];
      const prevTab = tabOrder.find((tab) => prevPath.includes(tab));
      const currentTab = tabOrder.find((tab) => currentPath.includes(tab));

      if (!prevTab || !currentTab) return null;

      const prevIndex = tabOrder.indexOf(prevTab);
      const currentIndex = tabOrder.indexOf(currentTab);

      return currentIndex > prevIndex ? "slide-right" : "slide-left";
    };

    const direction = getDirection();

    if (direction) {
      setIsTransitioning(true);

      // Сохраняем следующую страницу
      nextChildrenRef.current = children;

      // Старая страница становится невидимой мгновенно
      setDisplayChildren(nextChildrenRef.current);

      // Запускаем анимацию появления новой страницы
      setAnimationClass(
        direction === "slide-right" ? "slide-in-right" : "slide-in-left",
      );

      // Очищаем предыдущий таймаут
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Убираем класс анимации после завершения
      timeoutRef.current = setTimeout(() => {
        setAnimationClass("");
        setIsTransitioning(false);
        nextChildrenRef.current = null;
      }, 300);
    } else {
      // Если направление не определено, просто меняем страницу без анимации
      setDisplayChildren(children);
      setAnimationClass("");
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, children, displayChildren, isTransitioning]);

  // Очистка таймаута при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const keyframes = `
    @keyframes slideInFromRight {
      from { 
        transform: translateX(100%); 
        opacity: 0; 
      }
      to { 
        transform: translateX(0); 
        opacity: 1; 
      }
    }
    
    @keyframes slideInFromLeft {
      from { 
        transform: translateX(-100%); 
        opacity: 0; 
      }
      to { 
        transform: translateX(0); 
        opacity: 1; 
      }
    }
    
    .slide-in-right {
      animation: slideInFromRight 0.3s ease-out forwards;
    }
    
    .slide-in-left {
      animation: slideInFromLeft 0.3s ease-out forwards;
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <div
          className={animationClass}
          style={{ width: "100%", height: "100%" }}
        >
          {displayChildren}
        </div>
      </div>
    </>
  );
};
